import { AfterViewInit, ChangeDetectorRef, Component, ElementRef, Inject, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { AbstractControl, FormBuilder, FormGroup, ValidationErrors, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { RepositoryService } from '../../linq-utils/repository.service';
import { UserService } from '../user.service';
import { Subscription } from 'rxjs';
import { MSAL_GUARD_CONFIG, MsalGuardConfiguration, MsalService } from '@azure/msal-angular';
import { InteractionType } from '@azure/msal-browser';
import { GoogleSigninButtonDirective } from '@abacritt/angularx-social-login';
import { EMAIL_MAX, isValidEmail, PASSWORD_MAX, PASSWORD_MIN, USER_NAME_MAX, USER_NAME_MIN, UTMCampaign } from '../../common/user';
import { ERROR_CODE, getUserMessage, LinqException } from '../../common/exceptions';
import { IMAGE_LINQ } from '../../model-services/model-item-enums';
import { AppComponent } from '../../app.component';

const COMPONENT = "Landing screen component"

// a name of spaces would pass the length checks
const trimmedMin = (min:number) => (control:AbstractControl):ValidationErrors | null =>
    control.value && control.value.trim().length < min ? {minlength:true} : null

const validEmail = (control:AbstractControl):ValidationErrors | null =>
    !control.value || isValidEmail(control.value.trim()) ? null : {email:true}

const passwordsMatch = (group:AbstractControl):ValidationErrors | null => {
    const password = group.get('password')?.value, confirmation = group.get('passwordConfirmation')?.value
    return confirmation && password !== confirmation ? {passwordMismatch:true} : null
}

const problem = (err:any, fallback:string) => getUserMessage(err as LinqException) || fallback

export enum LANDING_TAB { LOGIN, REGISTER, RESET }

@Component({
    selector: 'app-landing-screen',
    templateUrl: './landing-screen.html',
    styleUrls: ['./landing-screen.scss'],
    standalone: false,
    providers: [GoogleSigninButtonDirective]
})
export class LandingScreenComponent implements OnInit, OnDestroy, AfterViewInit {
    IMAGE_LINQ=IMAGE_LINQ
    authForm: FormGroup;
    resetForm: FormGroup;
    registrationForm: FormGroup
    signingIn:boolean  =  false;
    code:string
    registered:boolean
    routeSub:Subscription
    querySub:Subscription
    selectedIndex:number = LANDING_TAB.LOGIN
    LANDING_TAB = LANDING_TAB

    // each form shows its own outcome, rather than a dialog over the page
    loginError = ''
    registerError = ''
    resetError = ''
    resetSent = false
    linkProblem = ''          // the invitation or reset link couldn't be used
    showPassword = false
    registering = false
    resetting = false

    USER_NAME_MIN = USER_NAME_MIN
    USER_NAME_MAX = USER_NAME_MAX
    PASSWORD_MIN = PASSWORD_MIN
    PASSWORD_MAX = PASSWORD_MAX
    EMAIL_MAX = EMAIL_MAX

    video:HTMLVideoElement
    @ViewChild('videoPlayer') videoPlayer:ElementRef


    constructor(private userService: UserService, private formBuilder: FormBuilder, private repoService:RepositoryService, private route:ActivatedRoute,
        @Inject(MSAL_GUARD_CONFIG) private msalGuardConfig: MsalGuardConfiguration, private authService: MsalService,
        private router: Router, public app:AppComponent) {
        this.authForm  =  this.formBuilder.group({
            email: ['', [Validators.required, validEmail]],
            password: ['', Validators.required],
        });

        this.registrationForm  =  this.formBuilder.group({
            email: ['', [Validators.required, validEmail]],
            password: ['', [Validators.required, Validators.minLength(PASSWORD_MIN), Validators.maxLength(PASSWORD_MAX)]],
            passwordConfirmation: ['', [Validators.required]],
            usersname: ['', [Validators.required, Validators.maxLength(USER_NAME_MAX), trimmedMin(USER_NAME_MIN)]],
        }, {validators: passwordsMatch});

        this.resetForm  =  this.formBuilder.group({
            resetEmail: ['', [Validators.required, validEmail]]
        });

        // a new attempt clears the last problem
        this.authForm.valueChanges.subscribe(() => this.loginError = '')
        this.registrationForm.valueChanges.subscribe(() => this.registerError = '')
        this.resetForm.valueChanges.subscribe(() => { this.resetError = ''; this.resetSent = false })

    }

    ngOnDestroy(): void {
        if (this.routeSub) this.routeSub.unsubscribe()
        if (this.querySub) this.querySub.unsubscribe()
    }

    async ngOnInit() {
        const SERVICE = "On init"

        this.userService.logout()

        this.routeSub = this.route.url.subscribe(async url => {
            if (url.length === 2 && (url[0].path === "activate" || url[0].path === "reset"))
            {
                const code = url[1].path
                let res:{registered:boolean, email:string}
                try {
                    res = await this.repoService.callSingleAPI(this.repoService.getData('api/public/isregistered/' + code), COMPONENT, SERVICE, undefined, undefined, undefined, undefined, false)
                }catch(err) {
                    // an old or used link: say so, and leave the page usable to sign in or ask for a new link
                    const e = err as LinqException
                    this.linkProblem = e?.msgCode === ERROR_CODE.ERR110 || e?.msgCode === ERROR_CODE.ERR111
                        ? "This link has expired or has already been used. Sign in, or use Reset password to get a new link."
                        : problem(err, "This link couldn't be checked. Please try again.")
                    this.selectedIndex = LANDING_TAB.LOGIN
                    return
                }
                this.code = code
                this.registered = res.registered
                this.selectedIndex = !this.registered ? LANDING_TAB.REGISTER : LANDING_TAB.LOGIN

                // the link belongs to this email, so it can't be changed
                this.registrationForm.controls['email'].setValue(res.email)
                this.registrationForm.controls['email'].disable()
                this.authForm.controls['email'].setValue(res.email)

                if (url[0].path === "activate" && this.registered) this.callActivation()
            }
            if (url.length === 1 && url[0].path === "register") {
                this.selectedIndex = LANDING_TAB.REGISTER
            }
        })

        this.querySub = this.route.queryParams.subscribe(async p => {
            if (p['utm_source']) {
                this.userService.utm = {
                    id: p['li_fat_id'],
                    source: p['utm_source'],
                    medium: p['utm_medium'],
                    name: p['utm_campaign'],
                    content: p['utm_content'],
                    term: p['utm_term'],
                    fbc: p['fbc'],
                    fbp: p['fbp']
                }
            }

            if (this.userService.utm?.id && this.userService.utm?.id.length > 0) {
                this.userService.utm.source = "linkedin"
            }else if (this.userService.utm?.fbp || this.userService.utm?.fbc) {
                this.userService.utm.source = "meta"
            }else {
                this.updateSocialIds();
            }

            await this.repoService.callSingleAPI(this.repoService.create('api/public/registerpage', this.userService.utm), COMPONENT, SERVICE)
        })

    }

    ngAfterViewInit(): void {
        setTimeout(() => {
            if (this.videoPlayer) {
                this.video = this.videoPlayer.nativeElement
                this.video.muted = true
                this.video.src = this.repoService.createCompleteRoute('api/help/temporal/video')
                this.video.load()
                this.video.play().catch(error => {
                    console.log("Autoplay prevented. Displaying fallback play button.");
                    // Show a custom UI play button or modal overlay here
                    //showPlayOverlayButton();
                });
            }
        }, 0)
    }

    updateSocialIds() {
        if (!this.userService.utm) this.userService.utm = {} as UTMCampaign

        const cookies = document.cookie.split(';');
        for (const c of cookies) {
            const [name, value] = c.trim().split('=');
            if (name === 'li_fat_id') {
                this.userService.utm.id = decodeURIComponent(value);
                this.userService.utm.source = 'linkedin';
                return              // if we find linked in use that
            }else if (name === '_fbp') {
                this.userService.utm.fbp = decodeURIComponent(value);
                this.userService.utm.source = 'meta';
            } else if (name === '_fbc') {
                this.userService.utm.fbc = decodeURIComponent(value);
                this.userService.utm.source = 'meta';
            }
        }
    }

    async signIn() {
        this.authForm.markAllAsTouched()
        if (this.authForm.invalid || this.signingIn) return

        this.signingIn = true
        try {
            const values = {email:this.authForm.value.email.trim(), password:this.authForm.value.password}
            await this.userService.signIn(this.code ? {...values, code:this.code} : values, false, false)
        }catch(err) {
            this.loginError = problem(err, "Signing in didn't work. Please try again.")
        }finally {
            this.signingIn = false
        }
    }

    async register() {
        const SERVICE = "Register"
        this.registrationForm.markAllAsTouched()
        if (this.registrationForm.invalid || this.registering) return

        // an invitation link sets up the account it was sent for
        if (this.code) return this.callActivation()

        this.registering = true
        try {
            const v = this.registrationForm.getRawValue()
            const details = {email:v.email.trim(), usersname:v.usersname.trim(), password:v.password, passwordConfirmation:v.passwordConfirmation}
            const body = this.userService.utm ? {utm:this.userService.utm, ...details} : details
            await this.repoService.callSingleAPI(this.repoService.create('api/public/register', body), COMPONENT, SERVICE, undefined, undefined, undefined, undefined, false)
            this.router.navigateByUrl(`registered`, {state:{email:details.email}});
        }catch(err) {
            this.registerError = problem(err, "Registration didn't work. Please try again.")
        }finally {
            this.registering = false
        }
    }

    // the server answers the same whether or not the email has an account
    async reset() {
        const SERVICE = "Reset password"
        this.resetForm.markAllAsTouched()
        if (this.resetForm.invalid || this.resetting) return

        this.resetting = true
        try {
            await this.repoService.callSingleAPI(this.repoService.create('api/public/reset', {email: this.resetForm.value.resetEmail.trim()}), COMPONENT, SERVICE, undefined, undefined, undefined, undefined, false)
            this.resetSent = true
        }catch(err) {
            this.resetError = problem(err, "The reset couldn't be requested. Please try again.")
        }finally {
            this.resetting = false
        }
    }

    async callActivation() {
        const SERVICE = "Call activation"
        this.registering = true
        try {
            const v = this.registrationForm.getRawValue()
            const res = await this.repoService.callSingleAPI(this.repoService.create('api/public/activate', {...v, usersname:(v.usersname || '').trim(), code:this.code}), COMPONENT, SERVICE, undefined, undefined, undefined, undefined, false)
            // setting the user goes where they start, as signing in does
            this.userService.setUser(res);
        }catch(err) {
            this.registerError = problem(err, "Your account couldn't be set up. Please try again.")
            if (this.registered) this.loginError = this.registerError
        }finally {
            this.registering = false
        }
    }

    passwordsDiffer() {
        return this.registrationForm.hasError('passwordMismatch') && this.registrationForm.controls['passwordConfirmation'].touched
    }

    loginMicrosoft() {
        if (this.msalGuardConfig.interactionType === InteractionType.Popup) {
            this.authService.loginPopup()
        } else {
            this.authService.loginRedirect();
        }
    }
}